/** @jsxImportSource @emotion/react */
/* eslint-disable no-useless-escape */

import styled from '@emotion/styled/macro'
import { BRAND_COLOR, getAppColors } from 'lib/constants'

import {
  Checkbox,
  CheckboxProps,
  InputLabel,
  Input,
  Typography,
  Box,
  FormControlLabel,
  Dialog,
  DialogContent,
} from '@material-ui/core'
import { ActionButton, ConditionalRender, StyledDialogActions, StyledDialogTitle } from 'components/widgets'
import { ERROR_COLOR, GRANTS, GRAY3_COLOR, TEXTFIELD_LABEL_COLOR } from 'lib/constants'
import { AlertTargetForwardType, trans, TranslationGroup, TranslationKey } from 'lib/types'
import { Grants, PopUpNotifications, Users } from 'models'
import { ProvisionAlertTarget, ProvisionedUser, provisionUser, resendInviteEmail } from 'models/api/users'
import { createOrUpdate } from 'models/Users'
import { isDuplicateAlertTargetError } from 'lib/utils/apiErrors'
import { isVoiceEnabled } from 'lib/utils/auth'
import { isValidEmail } from 'models/modelUtils'
import { ALL_ORGS_SELECTED } from 'lib/constants'
import { useState, memo, ChangeEvent, useMemo } from 'react'
import { AssignModal } from './AssignModal'
import PhoneInput, { Value } from 'react-phone-number-input'
import { isPossiblePhoneNumber } from 'react-phone-number-input'
import { useGlobal } from 'contexts/GlobalProvider'

// NEW: voice license checkbox row for Add User flow
import AssignVoiceLicenseCheckbox from 'components/partials/AssignVoiceLicenseCheckbox'

import type { License } from 'lib/types/license'

// v4 /grants/associate reads `org_id` from the request header and requires a valid UUID.
// Anything else (null, empty string, the ALL_PROPERTIES sentinel) makes the server 422.
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
const isUuid = (s: unknown): s is string => typeof s === 'string' && UUID_RE.test(s)

const UserWrapper = styled.div({
  width: '100%',
  alignContent: 'center',
})

const StyledContainer = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: '25px',
})

const StyledFormControl = styled.div({
  width: '100%',
  marginBottom: '15px',
})

const StyledInputLabel = styled(InputLabel)({
  color: TEXTFIELD_LABEL_COLOR,
  fontFamily: 'Montserrat',
  fontSize: '1.4rem',
  fontStyle: 'normal',
  fontWeight: 'bold',
  lineHeight: '1.5rem',
  letterSpacing: '0.1rem',
  textAlign: 'left',
  marginBottom: '20px',
})

const StyledInputErrorLabel = styled(InputLabel)({
  color: ERROR_COLOR,
  fontFamily: 'Montserrat',
  fontSize: '1.2rem',
  fontStyle: 'normal',
  fontWeight: 'bold',
  lineHeight: '1.5rem',
  letterSpacing: '0.1rem',
  textAlign: 'left',
  margin: '5px',
})

const StyledInput = styled(Input)({
  background: GRAY3_COLOR,
  borderRadius: '10px',
  padding: '5px 10px',
  fontFamily: 'Montserrat',
})

interface StyledCheckboxProps extends CheckboxProps {
  checkboxcolor: string
}

const StyledRow = styled.div({
  display: 'flex',
  justifyContent: 'space-between',
})

const StyledCheckbox = styled(Checkbox)<StyledCheckboxProps>(props => ({
  border: `1px solid ${getAppColors('--color-surface')}`,
  padding: '0',
  '&.Mui-checked': {
    color: props.checkboxcolor,
  },
}))

const StyledText = styled.span({
  fontFamily: 'Montserrat',
  fontStyle: 'normal',
  fontWeight: 'normal',
  fontSize: '1.6rem',
})

const StyledPhoneInput = styled(PhoneInput)({
  height: '28px',
  width: '100%',
  '.PhoneInputInput': {
    backgroundColor: 'blue',
    height: '100%',
    border: 'none',
    background: GRAY3_COLOR,
    borderRadius: '10px',
    padding: '5px 10px',
    fontFamily: 'Montserrat',
    width: '100%',
  },
  '.PhoneInputCountryIcon': {
    height: '20px',
    width: '30px',
    border: 'none',
  },
})

type Props = {
  orgId: string
  close: Function
  open: boolean
}

export function AddUserModal({ orgId, close, open }: Props) {
  const translation: TranslationGroup = trans.group(TranslationKey.USER_TABLE_VIEW)
  const common: TranslationGroup = trans.common()
  const auth: TranslationGroup = trans.group(TranslationKey.AUTH)

  const [email, setEmail] = useState('')
  const [subscribedEmail, setSubscribedEmail] = useState(false)
  // Both errors hold the message to render, so a client-side validation failure
  // and a server-side refusal can share one label per field. Empty means clear.
  const [emailError, setEmailError] = useState('')

  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')

  const [phone, setPhone] = useState<string>('')
  const [subscribedSms, setSubscribedSms] = useState(false)
  const [smsConsentChecked, setSmsConsentChecked] = useState(false)
  const [smsError, setSmsError] = useState('')

  const [submitting, setSubmitting] = useState(false)

  const [grantsMap, setGrantsMap] = useState({})

  // NEW: voice license selection (applied after user is created)
  const [assignVoiceLicense, setAssignVoiceLicense] = useState(false)

  // From global context
  // Note: if your GlobalContextType doesn’t yet include these fields, add them there too.
  const { setRefreshFlag, selectedOrg, licenses, reloadLicenses, licensesLoading } = useGlobal() as any

  // Entitlement, not capacity: a leftover Zello subdomain on a downgraded org
  // is not voice. `maxSeats` below is the separate question of how many seats
  // that entitlement carries.
  const hasVoiceSetup = useMemo(() => isVoiceEnabled(selectedOrg), [selectedOrg])

  const maxSeats = useMemo(() => {
    const v = (selectedOrg as any)?.zelloUserMax
    return typeof v === 'number' ? Number(v) : 0
  }, [selectedOrg?.id, (selectedOrg as any)?.zelloUserMax])

  const orgLicenses = useMemo<License[]>(() => {
    const list: License[] = (licenses || []) as License[]
    return list.filter(l => l?.orgId === orgId && l?.type === 'standalone_voice')
  }, [licenses, orgId])

  const assignedSeats = orgLicenses.length

  const licenseAvailable = useMemo(() => {
    // If maxSeats is 0/undefined, treat as not available until your backend rules are finalized
    if (!hasVoiceSetup) return false
    if (!maxSeats) return false
    return assignedSeats < maxSeats
  }, [hasVoiceSetup, maxSeats, assignedSeats])

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      handleSubmit()
    }
  }

  // The endpoint creates the grants named here and nothing more, so the set has
  // to be assembled explicitly. `dispatcher` is a UI-only combination of
  // portal_user and responder, `associate` is not caller-assignable, and admin
  // supersedes the three narrower grants — the same rules `updateGrants` applies.
  function grantsToProvision(): string[] {
    const checked = Object.entries(grantsMap)
      .filter(([, isChecked]) => isChecked)
      .map(([name]) => name)
      .filter(name => name !== GRANTS.DISPATCHER && name !== GRANTS.ASSOCIATE)

    if (checked.includes(GRANTS.ADMIN)) {
      return [GRANTS.ADMIN]
    }

    return checked.length ? checked : [GRANTS.PORTAL_USER]
  }

  function alertTargetsToProvision(): ProvisionAlertTarget[] {
    const targets: ProvisionAlertTarget[] = []
    if (subscribedEmail) targets.push({ forward_type: AlertTargetForwardType.EMAIL, forward: email })
    if (subscribedSms) targets.push({ forward_type: AlertTargetForwardType.SMS, forward: phone })
    return targets
  }

  // Provisioning identifies a user by email and never writes a name, so the name
  // the admin typed needs a follow-up update. It is deliberately skipped when the
  // matched user already has one: "Add User" grants membership in *this* org, and
  // the user record is global, so writing here would rename that person everywhere
  // on the strength of a per-property action. Renaming stays in Edit User.
  async function saveName(provisioned: ProvisionedUser) {
    const name = { firstName: firstName.trim(), lastName: lastName.trim() }
    if (!provisioned?.id || (!name.firstName && !name.lastName)) return

    if (provisioned.firstName || provisioned.lastName) {
      PopUpNotifications.fireInfo({ content: translation.existing_name_kept })
      return
    }

    // `createOrUpdate` notifies on failure and rethrows; the add itself already
    // succeeded, so the rejection is absorbed rather than reported twice.
    await createOrUpdate({ id: provisioned.id, ...name }).catch(() => null)
  }

  const handleSubmit = () => {
    if (submitting) return

    // Reject early when no real org is selected — otherwise the POST goes out with
    // org_id=ALL_PROPERTIES (or empty), which provisioning cannot resolve to an org.
    if (!isUuid(orgId) || orgId === ALL_ORGS_SELECTED) {
      PopUpNotifications.fireError({ content: translation.all_properties_selected })
      return
    }

    // email validation
    if (email.length) {
      if (!isValidEmail(email)) {
        setEmailError(common.email_error)
        return
      } else {
        setEmailError('')
      }
    }

    // phone validation
    const isValid = isPossiblePhoneNumber(phone || '')
    setSmsError('')

    if (!isValid && subscribedSms) {
      setSmsError(translation.invalid_phone)
      return
    }

    if (subscribedSms && !smsConsentChecked) {
      setSmsError(translation.sms_consent_required)
      return
    }

    // If they checked assign voice license but we know it isn't available, stop early
    if (assignVoiceLicense && (!hasVoiceSetup || !licenseAvailable)) {
      PopUpNotifications.fireError({ content: 'No voice licenses available for this organization.' })
      return
    }

    // Provisioning is one transaction — it writes the user, grants, subscriptions
    // and responder together, or it writes nothing. So the panel has to stay open
    // until the server answers: closing first is what let a rejected phone number
    // read as a successful add, with the operator seeing neither the warning nor
    // the fields they'd need to correct.
    setSubmitting(true)

    provisionUser(orgId, {
      email,
      grants: grantsToProvision(),
      alert_targets: alertTargetsToProvision(),
      voice_license: assignVoiceLicense,
    })
      .then(async provisioned => {
        close()
        PopUpNotifications.fireSuccess({ content: translation.user_added })

        await saveName(provisioned)

        // Provisioning writes rows only; the confirmation mail that the v4
        // associate endpoint used to send has to be asked for separately.
        await resendInviteEmail(email).catch(() => null)

        await Users.reload()
        await Grants.reload()
        if (assignVoiceLicense && typeof reloadLicenses === 'function') await reloadLicenses(orgId)
        setRefreshFlag(true)
      })
      .catch(error => {
        // A forward already claimed on this property is fixable right here, so it
        // gets named on the field that owns it rather than thrown at a toast.
        if (isDuplicateAlertTargetError(error)) {
          if (subscribedSms) setSmsError(translation.phone_in_use)
          else setEmailError(translation.email_in_use)
          return
        }
        PopUpNotifications.fireErrorObject(error)
      })
      .finally(() => setSubmitting(false))
  }

  function handlePhoneValueChange(num: Value) {
    setSmsError('')
    setPhone(num)
  }

  function handleEmailValueChange(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    setEmail(e.target.value.toLowerCase().replace(/\s/g, ''))
    setEmailError('')
  }

  return (
    <Dialog style={{ animationFillMode: 'forwards' }} open={open} onClose={() => null} maxWidth="sm" fullWidth>
      <StyledDialogTitle>ADD USER</StyledDialogTitle>
      <DialogContent>
        <UserWrapper>
          <StyledContainer>
            <div>
              <form>
                <StyledFormControl>
                  <StyledInputLabel htmlFor="user-email">{common.email}</StyledInputLabel>
                  <StyledInput
                    id="user-email"
                    disableUnderline
                    fullWidth
                    placeholder={translation.enter_email}
                    value={email}
                    type="email"
                    onChange={e => handleEmailValueChange(e)}
                    onKeyDown={handleKeyDown}
                  />
                  <StyledInputErrorLabel style={{ visibility: emailError ? 'visible' : 'hidden' }}>
                    {emailError || ' '}
                  </StyledInputErrorLabel>

                  <StyledRow>
                    <StyledText>Subscribe to email alerts</StyledText>
                    <StyledCheckbox
                      checkboxcolor={BRAND_COLOR}
                      checked={subscribedEmail}
                      onChange={() => setSubscribedEmail(!subscribedEmail)}
                      disabled={false}
                    />
                  </StyledRow>
                </StyledFormControl>

                <StyledFormControl>
                  <StyledInputLabel htmlFor="user-first-name">{common.name}</StyledInputLabel>
                  <Box css={{ display: 'flex', justifyContent: 'space-between' }}>
                    <StyledInput
                      id="user-first-name"
                      css={{ width: '48%' }}
                      disableUnderline
                      placeholder={auth.first_name}
                      value={firstName}
                      onChange={e => setFirstName(e.target.value)}
                      onKeyDown={handleKeyDown}
                    />
                    <StyledInput
                      id="user-last-name"
                      css={{ width: '48%' }}
                      disableUnderline
                      placeholder={auth.last_name}
                      value={lastName}
                      onChange={e => setLastName(e.target.value)}
                      onKeyDown={handleKeyDown}
                    />
                  </Box>
                </StyledFormControl>

                <StyledFormControl>
                  <StyledInputLabel htmlFor="user-phone">{common.phone}</StyledInputLabel>
                  <StyledPhoneInput
                    defaultCountry="US"
                    placeholder="Enter phone number"
                    value={phone}
                    onChange={handlePhoneValueChange}
                    international={false}
                  />

                  <StyledInputErrorLabel style={{ visibility: smsError ? 'visible' : 'hidden' }}>
                    {smsError || ' '}
                  </StyledInputErrorLabel>

                  <StyledRow>
                    <StyledText>Subscribe to sms alerts</StyledText>
                    <StyledCheckbox
                      checkboxcolor={BRAND_COLOR}
                      checked={subscribedSms}
                      onChange={() => {
                        setSmsError('')
                        setSubscribedSms(!subscribedSms)
                      }}
                      disabled={false}
                    />
                  </StyledRow>

                  {subscribedSms && (
                    <>
                      <Typography variant="body1">
                        Please confirm you would like to receive SMS notifications through React Mobile. Please note,
                        message and data rates may apply, and message frequency varies. Deliverability of messages is
                        subject to your carrier and is not guaranteed by React Mobile. For terms and conditions and our
                        privacy policy, please visit reactmobile.com. You can reply STOP to 25378 at any time to cancel
                        or HELP for help
                      </Typography>

                      <Box css={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: '5px' }}>
                        <FormControlLabel
                          control={
                            <StyledCheckbox
                              checkboxcolor={BRAND_COLOR}
                              checked={smsConsentChecked}
                              onChange={() => {
                                setSmsError('')
                                setSmsConsentChecked(!smsConsentChecked)
                              }}
                              name="sms-consent-checkbox"
                            />
                          }
                          label="I consent"
                        />
                      </Box>
                    </>
                  )}

                  {/* NEW: Assign Voice License checkbox (under SMS checkbox) */}
                  <ConditionalRender condition={isVoiceEnabled(selectedOrg)}>
                    <AssignVoiceLicenseCheckbox
                      org={selectedOrg}
                      checked={assignVoiceLicense}
                      setChecked={setAssignVoiceLicense}
                      hasVoiceSetupOverride={hasVoiceSetup}
                      licenseAvailableOverride={licenseAvailable && !licensesLoading}
                    />
                  </ConditionalRender>
                </StyledFormControl>
              </form>

              <AssignModal title={translation.assign_roles} orgId={orgId} setGrants={setGrantsMap} />
            </div>
          </StyledContainer>

          <StyledDialogActions>
            <ActionButton width="110px" height="28px" onClick={() => close()} disabled={submitting}>
              {common.cancel}
            </ActionButton>
            <ActionButton
              width="110px"
              height="28px"
              onClick={handleSubmit}
              loading={submitting}
              disabled={!!emailError || submitting}
            >
              {translation.add_user}
            </ActionButton>
          </StyledDialogActions>
        </UserWrapper>
      </DialogContent>
    </Dialog>
  )
}

// ✅ Fix: export the modal, not AssignModal
export default memo(AddUserModal)
